import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchPolicies, fetchPoliciesForMultipleUsers } from './policySlice';
import {
  Box,
  Typography,
  TextField,
  Paper,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Avatar,
  InputAdornment,
  Badge,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Stack,
  Popover,
  InputLabel,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  CircularProgress,
  Select
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useResponsiveFonts, truncateName, truncateVehicleModel, truncateText, truncateVehicleNumber, truncatePhoneNumber } from '../../utils/responsiveFonts';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import StatusBadge from '../../components/common/StatusBadge';
import { calculatePolicyStatus, parseDate } from '../../utils/dateUtils';
import { exportPoliciesToExcel } from '../../utils/exportPolicies';
import { getPolicyCountsByCategory } from '../../utils/countUtils';
import { COMPANIES, POLICY_TYPES } from '../../data/dropdowns';
//import { handlePageChangeWithScroll, navigateWithScrollReset } from '../../utils/ScrollRestoration';
import MobileBottomNavigation from '../../components/common/MobileBottomNavigation';
import { handleMobileRefresh } from '../../utils/mobileRefresh';
import { toast } from 'react-toastify';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);
import { useAdminStaffData } from '../../hooks/useAdminStaffData';
const MobilePolicyList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { policies, loading, initialized } = useSelector((state) => state.policies);
  const { user } = useSelector((state) => state.auth);
  const { userIds, isAdmin, loading: staffLoading } = useAdminStaffData();
  const fonts = useResponsiveFonts();

  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('motor');
  const [dropdownAnchor, setDropdownAnchor] = useState(null);
  const [displayedPolicies, setDisplayedPolicies] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showFabMenu, setShowFabMenu] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Filter state
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedPolicyTypes, setSelectedPolicyTypes] = useState({}); // Object keyed by category for tab-specific filtering
  const [showCompanyOptions, setShowCompanyOptions] = useState(false);
  const [showPolicyTypeOptions, setShowPolicyTypeOptions] = useState(false);
  const [isFilterClosing, setIsFilterClosing] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [showDateRangeCalendar, setShowDateRangeCalendar] = useState(null);

  // Export dialog state
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportDatePreset, setExportDatePreset] = useState('1month');
  const [exportCustomStart, setExportCustomStart] = useState('');
  const [exportCustomEnd, setExportCustomEnd] = useState('');
  const [showAdvancedDates, setShowAdvancedDates] = useState(false);
  const exportEndDateRef = useRef(null);

  const itemsPerPage = 10;
  const contentRef = useRef(null);

  // Refresh handler using shared refresh function
  const handleRefresh = async () => {
    if (isRefreshing) return; // Prevent multiple simultaneous refreshes

    try {
      await handleMobileRefresh({
        userId: user?.uid,
        dispatch,
        setRefreshing: setIsRefreshing
      });
      console.log('✅ PolicyList data refreshed');
    } catch (error) {
      console.error('❌ Error refreshing PolicyList:', error);
    }
  };

  const categories = ['motor', 'non-motor', 'life'];
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Wait for useAdminStaffData to resolve admin status and staff UIDs
    if (staffLoading) return;

    // Admin → fetch policies for multiple users
    if (isAdmin && userIds.length > 0) {
      console.log('Admin user (Mobile) - fetching aggregated policies:', userIds);
      dispatch(fetchPoliciesForMultipleUsers(userIds));
      return;
    }

    // Normal user → fetch own policies
    if (user?.uid && !isAdmin) {
      console.log('Staff user (Mobile) - fetching own policies');
      dispatch(fetchPolicies(user.uid));
    }
  }, [user?.uid, dispatch, isAdmin, userIds, staffLoading]);


  // Restore filter state from sessionStorage on mount
  useEffect(() => {
    const savedFilters = sessionStorage.getItem('mobilePolicyList_filterState');
    if (savedFilters) {
      try {
        const filters = JSON.parse(savedFilters);
        setSelectedCompanies(filters.selectedCompanies || []);
        setSelectedPolicyTypes(filters.selectedPolicyTypes || {});
        setShowCompanyOptions(filters.showCompanyOptions || false);
        setShowPolicyTypeOptions(filters.showPolicyTypeOptions || false);
        // Restore date range
        if (filters.dateRange) {
          setDateRange({
            start: filters.dateRange.start ? dayjs(filters.dateRange.start) : null,
            end: filters.dateRange.end ? dayjs(filters.dateRange.end) : null
          });
        }
      } catch (error) {
        console.error('Error restoring filter state:', error);
      }
    }
    // Delay setting isInitialMount to false to ensure all state updates complete
    setTimeout(() => {
      isInitialMount.current = false;
    }, 100);
  }, []); // Run only on mount

  // Save filter state to sessionStorage whenever filters change (but not on initial mount)
  useEffect(() => {
    // Skip saving on initial mount to avoid overwriting with empty state
    if (isInitialMount.current) {
      return;
    }

    const filterState = {
      selectedCompanies,
      selectedPolicyTypes,
      showCompanyOptions,
      showPolicyTypeOptions,
      dateRange: {
        start: dateRange.start ? dateRange.start.toISOString() : null,
        end: dateRange.end ? dateRange.end.toISOString() : null
      }
    };
    sessionStorage.setItem('mobilePolicyList_filterState', JSON.stringify(filterState));
  }, [selectedCompanies, selectedPolicyTypes, showCompanyOptions, showPolicyTypeOptions, dateRange]);

  // Restore category from navigation state or sessionStorage
  useEffect(() => {
    // First try to restore from navigation state
    if (location.state?.fromCategory) {
      console.log('MobilePolicyList: Restoring category from state:', location.state.fromCategory);
      setSelectedCategory(location.state.fromCategory);
    } else {
      // If no navigation state, check sessionStorage (for swipe back)
      const storedCategory = sessionStorage.getItem('mobilePolicyList_selectedCategory');
      if (storedCategory) {
        console.log('MobilePolicyList: Restoring category from sessionStorage:', storedCategory);
        setSelectedCategory(storedCategory);
        // Clear it after use
        sessionStorage.removeItem('mobilePolicyList_selectedCategory');
      }
    }
  }, [location.state]);

  // Scroll to top when category or filters change
  useEffect(() => {
    if (!isInitialMount.current && contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [selectedCategory, searchTerm, selectedCompanies, selectedPolicyTypes, dateRange]);


  // Filter policies based on selected category and search term
  const filteredPolicies = policies.filter((policy) => {
    // Exclude new leads
    if (policy.status === "New Lead") return false;

    const categoryMatch =
      selectedCategory === 'motor'
        ? policy.policyCategory === 'motor'
        : selectedCategory === 'non-motor'
          ? policy.policyCategory === 'non-motor'
          : policy.policyCategoryType === selectedCategory;

    if (!categoryMatch) return false;

    // Company filter
    if (selectedCompanies.length > 0 && !selectedCompanies.includes(policy.policyCompany)) {
      return false;
    }

    // Policy type filter (only for current tab)
    const currentTabPolicyTypes = selectedPolicyTypes[selectedCategory] || [];
    if (currentTabPolicyTypes.length > 0 && !currentTabPolicyTypes.includes(policy.policyType)) {
      return false;
    }

    // Date range filter (on createdAt timestamp)
    if (dateRange.start || dateRange.end) {
      const created = policy.createdAt ? (policy.createdAt.seconds ? dayjs(policy.createdAt.seconds * 1000) : dayjs(policy.createdAt)) : null;
      if (created && created.isValid()) {
        if (dateRange.start && created.isBefore(dateRange.start, 'day')) {
          return false;
        }
        if (dateRange.end && created.isAfter(dateRange.end, 'day')) {
          return false;
        }
      } else if (dateRange.start || dateRange.end) {
        // If date range is set but policy has no valid date, exclude it
        return false;
      }
    }

    const searchLower = searchTerm.toLowerCase();
    const hitsBase =
      (policy.name || '').toLowerCase().includes(searchLower) ||
      (policy.mobile || '').includes(searchTerm);

    if (selectedCategory === 'motor') {
      const searchClean = searchLower.replace(/-/g, "");
      const vehicleNumberClean = (policy.vehicleNumber || "")
        .toLowerCase()
        .replace(/-/g, "");
      const hitVehicle = vehicleNumberClean.includes(searchClean);
      return hitsBase || hitVehicle;
    }

    return hitsBase;
  });

  // Calculate counts for each category WITH filters applied but WITHOUT category restriction
  // This ensures each tab shows the count of policies in that category that match the current filters
  const policiesWithFiltersOnly = policies.filter((policy) => {
    // Exclude new leads
    if (policy.status === "New Lead") return false;

    // Company filter
    if (selectedCompanies.length > 0 && !selectedCompanies.includes(policy.policyCompany)) {
      return false;
    }


    // Date range filter (on createdAt timestamp)
    if (dateRange.start || dateRange.end) {
      const created = policy.createdAt ? (policy.createdAt.seconds ? dayjs(policy.createdAt.seconds * 1000) : dayjs(policy.createdAt)) : null;
      if (created && created.isValid()) {
        if (dateRange.start && created.isBefore(dateRange.start, 'day')) {
          return false;
        }
        if (dateRange.end && created.isAfter(dateRange.end, 'day')) {
          return false;
        }
      } else if (dateRange.start || dateRange.end) {
        // If date range is set but policy has no valid date, exclude it
        return false;
      }
    }

    return true;
  });

  const categoryCounts = getPolicyCountsByCategory(policiesWithFiltersOnly);

  // Sort by createdAt (newest first)
  const sortedPolicies = filteredPolicies.sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
    const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
    return dateB - dateA;
  });

  // Initialize displayed policies when filters change
  useEffect(() => {
    // Do not set displayed policies until initial data load has completed
    if (!initialized) return;

    if (sortedPolicies.length > 0) {
      // Show ALL policies initially, not just first page
      setDisplayedPolicies(sortedPolicies);
      setHasMore(false); // No more to load since we're showing all
    } else {
      setDisplayedPolicies([]);
      setHasMore(false);
    }
  }, [selectedCategory, searchTerm, policies.length, initialized, selectedCompanies.join(','), JSON.stringify(selectedPolicyTypes), dateRange]);

  // Infinite scroll logic
  const loadMorePolicies = useCallback(() => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);

    setTimeout(() => {
      setDisplayedPolicies(prev => {
        const currentLength = prev.length;
        // Recalculate sortedPolicies inside the callback to get fresh data
        const currentSortedPolicies = filteredPolicies.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
          const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
          return dateB - dateA;
        });
        const nextPolicies = currentSortedPolicies.slice(currentLength, currentLength + itemsPerPage);

        if (nextPolicies.length > 0) {
          setHasMore(currentLength + nextPolicies.length < currentSortedPolicies.length);
          return [...prev, ...nextPolicies];
        } else {
          setHasMore(false);
          return prev;
        }
      });

      setIsLoading(false);
    }, 300);
  }, [isLoading, hasMore, filteredPolicies, itemsPerPage]);

  // Scroll event listener for infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;

      console.log('Scroll detected:', { scrollTop, scrollHeight, clientHeight, isNearBottom, hasMore, isLoading });

      if (isNearBottom && hasMore && !isLoading) {
        console.log('Loading more policies...');
        loadMorePolicies();
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll, { passive: true });
      return () => contentElement.removeEventListener('scroll', handleScroll);
    }
  }, [hasMore, isLoading, loadMorePolicies]);

  // Show loading state until data is actually loaded
  if (loading || !initialized) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Typography>Loading policies...</Typography>
      </Box>
    );
  }

  const handleDropdownOpen = (event) => {
    setDropdownAnchor(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setDropdownAnchor(null);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    handleDropdownClose();
  };

  const handlePolicyClick = (policy) => {
    // Store current category in sessionStorage for swipe back support
    sessionStorage.setItem('mobilePolicyList_selectedCategory', selectedCategory);
    const navigationState = { policy, fromScreen: 'policies', fromCategory: selectedCategory };
    navigate(`/policy/${policy.id}`, { state: navigationState });
  };

  const handleFabMenuOpen = () => {
    setShowFabMenu(true);
  };

  const handleFabMenuClose = () => {
    setShowFabMenu(false);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const toNumber = (val) => {
    if (!val) return 0;
    const cleaned = String(val).replace(/[^\d.-]/g, '');
    const num = Number(cleaned);
    return isNaN(num) ? 0 : num;
  };

  // Filter handlers
  const handleFilterButtonClick = (event) => {
    // If filter is already open, close it (same behavior as Done button)
    if (filterAnchorEl) {
      handleFilterClose();
    } else {
      setFilterAnchorEl(event.currentTarget);
      setIsFilterClosing(false);
    }
  };

  const handleFilterClose = () => {
    setIsFilterClosing(true);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      setFilterAnchorEl(null);
      setIsFilterClosing(false);
    }, 200); // Match the collapse animation duration
  };

  const handleCompanyToggle = (company) => {
    setSelectedCompanies((prev) =>
      prev.includes(company)
        ? prev.filter((c) => c !== company)
        : [...prev, company]
    );
  };

  const handlePolicyTypeToggle = (policyType) => {
    setSelectedPolicyTypes((prev) => {
      const currentTypes = prev[selectedCategory] || [];
      const updatedTypes = currentTypes.includes(policyType)
        ? currentTypes.filter((t) => t !== policyType)
        : [...currentTypes, policyType];
      return { ...prev, [selectedCategory]: updatedTypes };
    });
  };

  const handleCompanyCheckboxChange = (checked) => {
    setShowCompanyOptions(checked);
    if (!checked) {
      setSelectedCompanies([]);
    }
  };

  const handlePolicyTypeCheckboxChange = (checked) => {
    setShowPolicyTypeOptions(checked);
    if (!checked) {
      setSelectedPolicyTypes({});
    }
  };

  const clearFilters = () => {
    setSelectedCompanies([]);
    setSelectedPolicyTypes({});
    setShowCompanyOptions(false);
    setShowPolicyTypeOptions(false);
    setDateRange({ start: null, end: null });
    setShowDateRangeCalendar(null);
  };

  const handleDateRangeCalendarChange = (date) => {
    const selectedDay = dayjs(date);

    if (showDateRangeCalendar === 'start') {
      // Setting start date
      setDateRange({
        start: selectedDay,
        end: dateRange.end && selectedDay.isAfter(dateRange.end) ? null : dateRange.end
      });
      // Automatically switch to end date selection if end is not set
      if (!dateRange.end) {
        setShowDateRangeCalendar('end');
      }
    } else if (showDateRangeCalendar === 'end') {
      // Setting end date
      // Validation: end date cannot be before start date
      if (selectedDay.isBefore(dateRange.start, 'day')) {
        // Don't set end date, keep calendar open for user to select valid date
        return;
      }
      setDateRange((prev) => ({
        start: prev.start,
        end: selectedDay
      }));
      // Close calendar after end date is selected
      setShowDateRangeCalendar(null);
    }
  };

  const getCalendarConfig = () => {
    try {
      const testDate = new Date();
      const testLocale = testDate.toLocaleDateString('en');
      return {
        calendarType: 'gregory',
        locale: 'en',
        maxDetail: 'month',
        minDetail: 'month'
      };
    } catch (error) {
      console.warn('Calendar locale detection failed, using fallback:', error);
      return {
        calendarType: 'gregory',
        locale: undefined,
        maxDetail: 'month',
        minDetail: 'month'
      };
    }
  };

  const dateRangeActive = Boolean(dateRange.start && dateRange.end);
  const activeFilterCount = selectedCompanies.length + ((selectedPolicyTypes[selectedCategory] || []).length) + (dateRangeActive ? 1 : 0);

  // ---- Export handler ----
  const handleExportExcel = async () => {
    console.log('[Export v2 Mobile] handleExportExcel called, preset:', exportDatePreset);
    setIsExporting(true);

    try {
      const now = dayjs();
      let startDate, endDate;

      if (exportDatePreset === 'custom') {
        if (!exportCustomStart || !exportCustomEnd) {
          toast.error('Please select both start and end dates');
          return;
        }

        const d1 = dayjs(exportCustomStart);
        const d2 = dayjs(exportCustomEnd);

        if (!d1.isValid() || !d2.isValid()) {
          toast.error('Invalid date selection');
          return;
        }

        if (d1.isAfter(d2)) {
          endDate = d1.endOf('day');
          startDate = d2.startOf('day');
        } else {
          endDate = d2.endOf('day');
          startDate = d1.startOf('day');
        }

      } else {
        endDate = now.endOf('day');
        if (exportDatePreset === '3months') {
          startDate = now.subtract(3, 'month').startOf('day');
        } else if (exportDatePreset === '6months') {
          startDate = now.subtract(6, 'month').startOf('day');
        } else if (exportDatePreset === '1year') {
          startDate = now.subtract(1, 'year').startOf('day');
        } else {
          startDate = now.subtract(1, 'month').startOf('day');
        }
      }

      if (!policies || policies.length === 0) {
        toast.info('No policies found to export.');
        return;
      }

      const filtered = policies.filter((p) => {
        if (p.status === 'New Lead') return false;

        // Use createdAt for range filtering (expiryDate is often far in the future)
        const pDate = p.createdAt || p.expiryDate;
        if (!pDate) return false;

        let parsed;
        // Handle ISO 8601 strings like "2026-03-13T06:11:27.279Z"
        if (typeof pDate === 'string' && pDate.includes('T')) {
          parsed = new Date(pDate);
        } else {
          parsed = parseDate(pDate);
        }
        if (!parsed || isNaN(parsed.getTime())) return false;

        const targetDate = dayjs(parsed);
        if (!targetDate.isValid()) return false;

        return !targetDate.isBefore(startDate) && !targetDate.isAfter(endDate);
      });

      if (filtered.length === 0) {
        toast.info('No policies found for the selected date range.');
        return;
      }

      const counts = exportPoliciesToExcel(filtered);
      toast.success(`Download started: ${counts.motorCount} motor, ${counts.nonMotorCount} other policies.`);
      setShowExportDialog(false);
    } catch (error) {
      console.error('[Export v2 Mobile] Export failed:', error);
      toast.error('Export error: ' + (error.message || 'Unknown error'));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box className="mobile-layout mobile-content-container" sx={{ width: '100vw', height: '100vh', overflow: 'hidden', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column', position: 'relative', overscrollBehavior: 'none' }}>
      {/* Header */}
      <Box sx={{ backgroundColor: '#0063F7', color: 'white', px: 2, py: 1, height: 70, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1004 }}>
        <Typography sx={{
          fontSize: fonts.title.xs,
          fontWeight: 600
        }}>
          Policies
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => {
              setExportDatePreset('1month');
              setExportCustomStart('');
              setExportCustomEnd('');
              setShowExportDialog(true);
            }}
            sx={{ color: 'white' }}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton
            onClick={handleRefresh}
            disabled={isRefreshing}
            sx={{
              color: 'white',
              '&:disabled': {
                opacity: 0.6
              }
            }}
          >
            <RefreshIcon />
          </IconButton>
          <IconButton
            onClick={() => setShowSearch(!showSearch)}
            sx={{ color: 'white' }}
          >
            <SearchIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Search Bar */}
      <Box sx={{
        position: 'fixed',
        top: 70,
        left: 0,
        right: 0,
        zIndex: 1003,
        backgroundColor: showSearch ? 'white' : 'transparent',
        pointerEvents: showSearch ? 'all' : 'none',
        borderBottom: showSearch ? '1px solid #e0e0e0' : 'none',
        transition: 'all 0.3s ease'
      }}>
        <Collapse in={showSearch}>
          <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 1.5, height: 70, boxSizing: 'border-box' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by name, number or vehicle"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#757575' }} />
                  </InputAdornment>
                )
              }}
            />
          </Box>
        </Collapse>
      </Box>


      {/* Category Tabs Section */}
      <Box sx={{
        position: 'fixed',
        top: showSearch ? 140 : 70,
        left: 0,
        right: 0,
        zIndex: 1002,
        backgroundColor: '#f5f5f5',
        px: 2,
        pt: 2.5, // Increased gap from header
        pb: 2,
        transition: 'top 0.3s ease'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.5 }}>
          {categories.map((category) => (
            <Box
              key={category}
              onClick={() => setSelectedCategory(category)}
              sx={{
                flex: 1,
                py: 0.6,
                px: 1,
                borderRadius: '15px',
                backgroundColor: selectedCategory === category ? '#E3F2FD' : 'white',
                border: `1px solid ${selectedCategory === category ? '#0063F7' : '#e0e0e0'}`,
                cursor: 'pointer',
                textAlign: 'center',
                minWidth: 0,
                transition: 'all 0.2s ease'
              }}
            >
              <Box sx={{
                fontSize: '13.5px !important',
                fontWeight: 500,
                color: selectedCategory === category ? '#0063F7' : '#666',
                textTransform: 'capitalize',
                whiteSpace: 'nowrap',
                letterSpacing: '-0.02em',
                lineHeight: 1.2
              }}>
                {category === 'motor' ? `Motor (${categoryCounts.motor || 0})` :
                  category === 'life' ? `Life (${categoryCounts.life || 0})` :
                    `Non-Motor (${categoryCounts['non-motor'] || 0})`}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>




      {/* Policy Cards */}
      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          py: 2,
          pb: 10,
          mt: showSearch ? '205px' : '135px',
          transition: 'margin-top 0.3s ease',
          overflowX: 'hidden',
          backgroundColor: '#f5f5f5'
        }}
      >

        {displayedPolicies.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography sx={{ color: '#666', mb: 1 }}>
              No policies found
            </Typography>
            <Typography sx={{ color: '#999', fontSize: '14px' }}>
              {searchTerm ? 'Try adjusting your search' : 'No policies in this category'}
            </Typography>
          </Box>
        ) : (
          <>
            {displayedPolicies.map((policy) => (
              <Paper
                key={policy.id}
                onClick={() => handlePolicyClick(policy)}
                sx={{
                  p: { xs: 0.8, sm: 1, md: 1.5 },
                  mb: 1.5,
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  width: '100%',
                  minWidth: '100%',
                  maxWidth: '100%',
                  flex: '1 1 100%',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }
                }}
              >
                {/* Company Name */}
                <Typography sx={{
                  fontSize: '16px !important',
                  fontWeight: 600,
                  color: '#0063F7',
                  mb: 0.8,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {policy.policyCompany || 'Insurance Company'}
                </Typography>

                {/* 2 Column Layout */}
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr',
                  gap: { xs: 1, sm: 1.5 },
                  alignItems: 'flex-start',
                  width: '100%',
                  overflow: 'hidden'
                }}>
                  {/* Column 1: Policy Details */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0, overflow: 'hidden' }}>
                    <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                      {selectedCategory === 'motor' ? (
                        <>
                          <Typography sx={{
                            fontSize: '16px !important',
                            fontWeight: 500,
                            color: '#212121',
                            mb: 0.3,
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {policy.vehicleMake && policy.vehicleModel ?
                              `${policy.vehicleMake} ${policy.vehicleModel} `.trim() :
                              (policy.vehicleMake || policy.vehicleModel || 'Vehicle')
                            }
                          </Typography>
                          <Typography sx={{
                            fontSize: '14px !important',
                            color: '#757575',
                            lineHeight: 1.1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            '&.MuiTypography-root': {
                              fontSize: '14px !important'
                            }
                          }}>
                            {policy.vehicleNumber || 'N/A'}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography sx={{
                            fontSize: '16px !important',
                            fontWeight: 500,
                            color: '#212121',
                            mb: 0.3,
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {policy.policyType || 'Policy'}
                          </Typography>
                          <Typography sx={{
                            fontSize: '14px !important',
                            color: '#757575',
                            lineHeight: 1.1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            '&.MuiTypography-root': {
                              fontSize: '14px !important'
                            }
                          }}>
                            ₹ {policy.premiumAmount ? toNumber(policy.premiumAmount).toLocaleString() : '0'}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* Column 2: Status */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <StatusBadge
                      status={calculatePolicyStatus(policy.expiryDate)}
                      size="small"
                    />
                  </Box>
                </Box>
              </Paper>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography sx={{ fontSize: '14px', color: '#757575' }}>
                  Loading more policies...
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Filter Box */}
      {
        Boolean(filterAnchorEl) && (
          <Box
            data-filter-box="true"
            sx={{
              position: 'fixed',
              bottom: 170, // Position above FAB (FAB is at 100px + 56px height + 14px spacing)
              right: '2vw',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              zIndex: 1300,
              width: '96vw',
              maxWidth: 500,
              maxHeight: 'calc(100vh - 250px)',
              overflowY: 'auto',
              p: 1.5,
              transformOrigin: 'bottom right',
              animation: isFilterClosing
                ? 'collapseToFab 0.2s ease-in forwards'
                : 'expandFromFab 0.3s ease-out',
              '@keyframes expandFromFab': {
                '0%': {
                  opacity: 0,
                  transform: 'scale(0.3)',
                  transformOrigin: 'bottom right'
                },
                '100%': {
                  opacity: 1,
                  transform: 'scale(1)',
                  transformOrigin: 'bottom right'
                }
              },
              '@keyframes collapseToFab': {
                '0%': {
                  opacity: 1,
                  transform: 'scale(1)',
                  transformOrigin: 'bottom right'
                },
                '100%': {
                  opacity: 0,
                  transform: 'scale(0.3)',
                  transformOrigin: 'bottom right'
                }
              }
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Stack spacing={1.5}>
              {/* Company Filter - Only show if user has multiple companies */}
              {(user?.hasMultipleCompanies !== false) && (
                <>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={showCompanyOptions}
                            onChange={(e) => handleCompanyCheckboxChange(e.target.checked)}
                          />
                        }
                        label={<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Company</Typography>}
                      />
                    </Box>
                    {showCompanyOptions && (
                      <Box sx={{ mt: 0.75, ml: 2 }}>
                        <Box sx={{ maxHeight: 180, overflowY: 'auto', pr: 1 }}>
                          <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 0.5
                          }}>
                            {(() => {
                              const companies = selectedCategory === 'motor'
                                ? COMPANIES.motor
                                : selectedCategory === 'life'
                                  ? COMPANIES.life
                                  : COMPANIES.non_motor;

                              return companies.map((company) => (
                                <FormControlLabel
                                  key={company}
                                  control={
                                    <Checkbox
                                      checked={selectedCompanies.includes(company)}
                                      onChange={() => handleCompanyToggle(company)}
                                      size="small"
                                      sx={{ p: 0.5 }}
                                    />
                                  }
                                  label={company}
                                  sx={{
                                    margin: 0,
                                    '& .MuiFormControlLabel-label': {
                                      fontSize: 12,
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      maxWidth: '100px'
                                    }
                                  }}
                                />
                              ));
                            })()}
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ my: 0.75 }} />
                </>
              )}

              {/* Policy Type Filter */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showPolicyTypeOptions}
                        onChange={(e) => handlePolicyTypeCheckboxChange(e.target.checked)}
                      />
                    }
                    label={<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Policy Type</Typography>}
                  />
                </Box>
                {showPolicyTypeOptions && (
                  <Box sx={{ mt: 0.75, ml: 2 }}>
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 0.5
                    }}>
                      {(() => {
                        const policyTypes = selectedCategory === 'motor'
                          ? POLICY_TYPES.motor
                          : selectedCategory === 'life'
                            ? POLICY_TYPES.life
                            : POLICY_TYPES.health;

                        return policyTypes.map((policyType) => (
                          <FormControlLabel
                            key={policyType}
                            control={
                              <Checkbox
                                checked={(selectedPolicyTypes[selectedCategory] || []).includes(policyType)}
                                onChange={() => handlePolicyTypeToggle(policyType)}
                                size="small"
                                sx={{ p: 0.5 }}
                              />
                            }
                            label={policyType}
                            sx={{
                              margin: 0,
                              '& .MuiFormControlLabel-label': {
                                fontSize: 12,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '100px'
                              }
                            }}
                          />
                        ));
                      })()}
                    </Box>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 0.75 }} />

              {/* Date Range Filter */}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Date Range</Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Button
                    variant={showDateRangeCalendar === 'start' ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setShowDateRangeCalendar(showDateRangeCalendar === 'start' ? null : 'start')}
                    sx={{
                      flex: 1,
                      fontSize: '0.75rem',
                      '&:hover': {
                        backgroundColor: showDateRangeCalendar === 'start' ? '#0056d6' : '#E0EBFF',
                        borderColor: '#E0EBFF'
                      }
                    }}
                  >
                    {dateRange.start ? dateRange.start.format('DD MMM') : 'START'}
                  </Button>
                  <Button
                    variant={showDateRangeCalendar === 'end' ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => {
                      if (dateRange.start) {
                        setShowDateRangeCalendar(showDateRangeCalendar === 'end' ? null : 'end');
                      }
                    }}
                    sx={{
                      flex: 1,
                      fontSize: '0.75rem',
                      '&:hover': {
                        backgroundColor: showDateRangeCalendar === 'end' ? '#0056d6' : '#E0EBFF',
                        borderColor: '#E0EBFF'
                      }
                    }}
                    disabled={!Boolean(dateRange.start)}
                  >
                    {dateRange.end ? dateRange.end.format('DD MMM') : 'END'}
                  </Button>
                </Stack>
                {showDateRangeCalendar && (
                  <Box
                    sx={{
                      position: 'relative',
                      zIndex: 1300,
                      boxShadow: 3,
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      width: '100%',
                      overflow: 'hidden',
                      padding: '6px',
                      mt: 1,
                      '& .react-calendar': {
                        width: '100%',
                        maxWidth: '100%',
                        minHeight: '160px',
                        border: 'none',
                        fontFamily: 'Roboto',
                        '&__navigation': {
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0 2px',
                          margin: '0 0 4px 0',
                          '&__label': {
                            minWidth: '110px',
                            textAlign: 'center',
                            flexGrow: 0,
                            fontWeight: '600 !important',
                            margin: 0,
                            padding: '2px 0',
                            fontSize: '12px',
                            lineHeight: '1.1'
                          },
                          '&__arrow': {
                            minWidth: '24px',
                            padding: '2px 0px',
                            margin: 0,
                            fontSize: '16px'
                          }
                        },
                        '&__month-view__weekdays': {
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '0 2px',
                          marginBottom: '1px',
                          '&__weekday': {
                            width: '26px',
                            padding: '1px 0',
                            textAlign: 'center',
                            fontSize: '9px',
                            fontWeight: '600 !important',
                            textTransform: 'uppercase',
                            color: '#666 !important',
                            backgroundColor: 'transparent !important',
                            border: 'none !important',
                            '& abbr': {
                              textDecoration: 'none',
                              display: 'inline-block',
                              width: '100%',
                              textAlign: 'center'
                            }
                          }
                        },
                        '&__month-view__days': {
                          display: 'grid !important',
                          gridTemplateColumns: 'repeat(7, 1fr)',
                          gap: '1px',
                          padding: '0 2px',
                          '&__day': {
                            width: '26px',
                            height: '26px',
                            margin: '0 auto',
                            fontWeight: '600 !important',
                            padding: '0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            '&--neighboringMonth': {
                              opacity: 0.5
                            },
                            '&--weekend': {
                              color: '#212121 !important'
                            },
                            '&--now': {
                              fontWeight: 'bold',
                              color: '#004DC2 !important',
                              backgroundColor: 'transparent !important',
                              '&:enabled:hover': {
                                backgroundColor: '#E0EBFF !important'
                              }
                            }
                          }
                        }
                      },
                      '& .react-calendar__tile': {
                        fontFamily: 'Roboto',
                        fontWeight: 500,
                        fontSize: '13px',
                        color: '#212121 !important',
                        transition: 'all 0.2s ease',
                        '&:enabled:hover': {
                          backgroundColor: '#E0EBFF !important',
                          color: '#212121 !important'
                        },
                        '&--active, &--active:enabled, &--active:enabled:hover': {
                          backgroundColor: '#004DC2 !important',
                          color: '#fff !important',
                          borderRadius: '50%',
                          border: 'none',
                          boxShadow: 'none',
                          outline: 'none'
                        }
                      },
                      "& .react-calendar__tile--active, & .react-calendar__tile--active:enabled": {
                        backgroundColor: '#004DC2 !important',
                        color: '#fff !important',
                      },
                      "& .react-calendar__month-view__days__day--weekend": {
                        color: '#212121 !important',
                      },
                      "& .react-calendar__tile:not(.react-calendar__tile--active):enabled:hover": {
                        backgroundColor: '#E0EBFF !important',
                      },
                      "& .react-calendar__navigation button": {
                        color: '#333 !important',
                        background: 'none !important',
                        fontWeight: '600 !important',
                        border: 'none !important',
                        outline: 'none !important',
                        '&:hover': {
                          backgroundColor: '#E0EBFF !important',
                          color: '#333 !important',
                        },
                        '&:focus': {
                          backgroundColor: 'transparent !important',
                          outline: 'none !important',
                          boxShadow: 'none !important',
                        },
                        '&:active': {
                          backgroundColor: 'transparent !important',
                        },
                      },
                      "& .react-calendar__navigation__label": {
                        color: '#333 !important',
                        background: 'transparent !important',
                        fontWeight: '600 !important',
                        '&:hover': {
                          backgroundColor: 'transparent !important',
                        },
                        '&:focus': {
                          backgroundColor: 'transparent !important',
                          outline: 'none !important',
                        },
                      },
                    }}
                  >
                    {(() => {
                      try {
                        return (
                          <Calendar
                            value={dateRange.start?.toDate() || dateRange.end?.toDate()}
                            onChange={handleDateRangeCalendarChange}
                            {...getCalendarConfig()}
                          />
                        );
                      } catch (error) {
                        console.error('Date range calendar rendering failed:', error);
                        return (
                          <Box sx={{ p: 2, textAlign: 'center', color: 'error.main' }}>
                            Calendar could not be loaded.
                          </Box>
                        );
                      }
                    })()}
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', mt: 0.5 }}>
                  {showDateRangeCalendar ? `Select date` : 'Click to select date range'}
                </Typography>
              </Box>

              <Divider sx={{ my: 0.75 }} />

              {/* Action Buttons */}
              <Stack direction="row" spacing={1.5} sx={{ pt: 0.5 }}>
                <Button
                  onClick={clearFilters}
                  size="small"
                  sx={{
                    flex: 1,
                    '&:hover': {
                      backgroundColor: '#F5F5F5'
                    }
                  }}
                >
                  Clear filters
                </Button>
                <Button
                  onClick={handleFilterClose}
                  size="small"
                  variant="contained"
                  sx={{
                    flex: 1
                  }}
                >
                  Done
                </Button>
              </Stack>
            </Stack>
          </Box >
        )
      }

      {/* Filter FAB Button */}
      <Box
        onClick={handleFilterButtonClick}
        sx={{
          position: 'fixed',
          bottom: 100, // Above the bottom navigation (80px height + 20px padding)
          right: { xs: 16, sm: 24 },
          width: { xs: 56, sm: 64 },
          height: { xs: 56, sm: 64 },
          backgroundColor: '#0063F7',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(0, 99, 247, 0.3)',
          zIndex: 1001,
          '&:active': {
            transform: 'scale(0.95)',
          },
          '&:hover': {
            backgroundColor: '#0051d4',
            boxShadow: '0 6px 20px rgba(0, 99, 247, 0.4)',
          },
        }}
      >
        <Badge
          color="primary"
          badgeContent={activeFilterCount}
          invisible={activeFilterCount === 0}
        >
          <FilterAltOutlinedIcon sx={{ fontSize: 24, color: 'white' }} />
        </Badge>
      </Box>



      {/* Bottom Navigation */}
      <MobileBottomNavigation isAdmin={isAdmin} contentRef={contentRef} />

      {/* FAB Menu */}
      {
        showFabMenu && (
          <Box sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} onClick={handleFabMenuClose}>
            <Box sx={{
              backgroundColor: 'white',
              borderRadius: '16px',
              p: 3,
              mx: 4,
              minWidth: '200px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
            }} onClick={(e) => e.stopPropagation()}>
              <Typography sx={{
                fontSize: '18px',
                fontWeight: 600,
                color: '#212121',
                mb: 2,
                textAlign: 'center'
              }}>
                Add New
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  onClick={() => {
                    handleFabMenuClose();
                    navigate('/upload-policy');
                  }}
                  sx={{
                    justifyContent: 'flex-start',
                    p: 2,
                    borderRadius: '8px',
                    textTransform: 'none',
                    color: '#212121'
                  }}
                >
                  Upload Policy
                </Button>
                <Button
                  onClick={() => {
                    handleFabMenuClose();
                    // Add new policy functionality
                  }}
                  sx={{
                    justifyContent: 'flex-start',
                    p: 2,
                    borderRadius: '8px',
                    textTransform: 'none',
                    color: '#212121'
                  }}
                >
                  Add Policy
                </Button>
              </Box>
            </Box>
          </Box>
        )
      }

      {/* Export Dialog */}
      <Dialog
        open={showExportDialog}
        onClose={() => !isExporting && setShowExportDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            mx: 2,
            p: 1,
          }
        }}
      >
        <DialogTitle sx={{
          fontFamily: 'Roboto Slab',
          fontWeight: 700,
          fontSize: '24px',
          lineHeight: '34px',
          color: '#212121',
          pb: 0.5,
        }}>
          Download Policy Data
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Roboto', fontSize: '14px', color: '#6B7280', mb: 2 }}>
            Select a date range and download your policy data.
          </Typography>

          <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: '14px', color: '#374151', mb: 1 }}>
            Date Range
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Select
              value={exportDatePreset}
              onChange={(e) => {
                setExportDatePreset(e.target.value);
                if (e.target.value !== 'custom') {
                  setShowAdvancedDates(false);
                }
              }}
              size="small"
              sx={{
                borderRadius: '8px',
                fontSize: '14px',
                width: '55%',
                '& .MuiSelect-select': { py: 1 },
              }}
            >
              <MenuItem value="1month">Last 1 Month</MenuItem>
              <MenuItem value="3months">Last 3 Months</MenuItem>
              <MenuItem value="6months">Last 6 Months</MenuItem>
              <MenuItem value="1year">Last 1 Year</MenuItem>
              <MenuItem value="custom" sx={{ display: 'none' }}>Custom Range</MenuItem>
            </Select>

            <Typography
              onClick={() => {
                setShowAdvancedDates(!showAdvancedDates);
                if (!showAdvancedDates) setExportDatePreset('custom');
                else setExportDatePreset('1month');
              }}
              sx={{
                fontFamily: 'Roboto',
                fontSize: '12px',
                color: '#0063F7',
                cursor: 'pointer',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Advanced Filter
            </Typography>
          </Box>

          {/* Custom date pickers */}
          {showAdvancedDates && (
            <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5 }}>
              <TextField
                type="date"
                label="Start Date (Latest)"
                value={exportCustomStart}
                onChange={(e) => {
                  setExportCustomStart(e.target.value);
                  // Auto-open end date calendar
                  if (e.target.value) {
                    setTimeout(() => {
                      exportEndDateRef.current?.showPicker?.();
                    }, 100);
                  }
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: dayjs().format('YYYY-MM-DD') }}
                size="small"
                sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '14px' } }}
              />
              <TextField
                type="date"
                label="End Date (Earlier)"
                inputRef={exportEndDateRef}
                value={exportCustomEnd}
                onChange={(e) => setExportCustomEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: exportCustomStart || dayjs().format('YYYY-MM-DD') // Cannot be after Start Date
                }}
                size="small"
                sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '14px' } }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setShowExportDialog(false)}
            disabled={isExporting}
            variant="outlined"
            sx={{
              flex: 1,
              height: '60px',
              borderRadius: '6px',
              borderColor: '#B00020',
              color: '#B00020',
              fontFamily: 'Roboto',
              fontWeight: 400,
              fontSize: '20px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'inherit',
                color: '#B00020',
                boxShadow: 10,
                borderColor: '#B00020'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleExportExcel}
            disabled={isExporting}
            startIcon={isExporting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <DownloadIcon sx={{ fontSize: 18 }} />}
            sx={{
              flex: 1,
              height: '60px',
              borderRadius: '6px',
              backgroundColor: '#0063F7 !important',
              fontFamily: 'Roboto',
              fontWeight: 400,
              fontSize: '20px',
              lineHeight: '24px',
              letterSpacing: '0.01em',
              textTransform: 'none',
              '&:disabled': {
                backgroundColor: '#CCCCCC !important',
                color: '#666666 !important',
              },
              '&:hover': {
                backgroundColor: '#0063F7 !important',
                color: '#FFFFFF !important',
                boxShadow: 10
              }
            }}
          >
            {isExporting ? 'Generating...' : 'Download'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box >
  );
};

export default MobilePolicyList;
